import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

/**
 * @typedef {Object} MasonryItem
 * @property {string|number} id
 * @property {number} width
 * @property {number} height
 * @property {React.ReactNode} component
 */

/**
 * @typedef {Object} MasonryListProps
 * @property {MasonryItem[]} [items] - Array of items to display in the masonry layout.
 * @property {number} [gap] - Gap between items in px.
 * @property {number} [numColumns] - Number of columns in the masonry layout (you can change this at any time for responsive layouts, etc)
 * @property {Function} [onEndReached] - Callback function that is called when the end of the list is scrolled to.
 * @property {Function} [onScroll] - Callback function that is called on scroll events.
 * @property {React.ReactNode} children - Arbitrary child components to render inside the masonry list apart from the `items`
 * @property {boolean} [isLoading] - Whether the list is currently loading more items (used to prevent multiple calls to `onEndReached`).
 * @property {React.ReactNode} [ListEmptyComponent] - Component to render when the list is empty.
 */

/**
 * Vertically-scrollable div that displays items in a masonry layout.
 * Uses virtualization to efficiently render only the visible items.
 */
const MasonryList = forwardRef(
  /**
   * @param {MasonryListProps} props
   * @param {React.Ref} ref - The scrollable area
   */
  function MasonryList(
    { items = [], gap = 0, numColumns = 1, onEndReached, onScroll, children, isLoading = false, ListEmptyComponent },
    ref
  ) {
    const scrollViewRef = useRef(null);
    const childrenRef = useRef({});
    const hasCalledOnEndReached = useRef(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [childrenHeight, setChildrenHeight] = useState(0);

    const columnWidth = (containerWidth - gap * (numColumns + 1)) / numColumns;

    const previousItemLayouts = useRef({});
    const previousConfig = useRef({
      numColumns,
      columnWidth,
      columnHeights: Array(numColumns).fill(0),
      containerHeight: 0,
    });

    const itemLayouts = useMemo(() => {
      const shouldRecalculateAll = true;
      if (containerWidth === 0 || columnWidth === 0) {
        return [];
      }

      let layouts = { ...previousItemLayouts.current };
      let columnHeights = [...previousConfig.current.columnHeights];

      if (
        shouldRecalculateAll ||
        numColumns !== previousConfig.current.numColumns ||
        columnWidth !== previousConfig.current.columnWidth
      ) {
        previousItemLayouts.current = {};
        layouts = {};
        columnHeights = Array(numColumns).fill(0);
      }

      if (!items || items.length === 0) {
        previousConfig.current.containerHeight = 0;
        return [];
      }

      items.forEach((item) => {
        if (shouldRecalculateAll || !layouts[item.id]) {
          const columnIndex = columnHeights.indexOf(Math.min(...columnHeights));
          const x = gap + columnIndex * (columnWidth + gap);
          const y = gap + columnHeights[columnIndex];
          const height = (item.height / item.width) * columnWidth;

          layouts[item.id] = { x, y, width: columnWidth, height, item };
          columnHeights[columnIndex] += height + gap;
        }
      });

      const bottomPadding = 200;
      let containerHeight = Math.max(0, Math.max(...columnHeights) - gap + bottomPadding);
      if (Number.isNaN(containerHeight)) {
        containerHeight = 0;
      }

      previousItemLayouts.current = layouts;
      previousConfig.current = { numColumns, columnWidth, columnHeights, containerHeight };

      return Object.values(layouts);
    }, [items, numColumns, columnWidth, containerWidth, gap]);

    const visibleItems = useMemo(() => {
      const startOffset = scrollOffset - childrenHeight;
      const endOffset = scrollOffset + viewportHeight - childrenHeight;

      return itemLayouts.filter(({ y, height }) => y + height > startOffset && y < endOffset);
    }, [itemLayouts, scrollOffset, viewportHeight, childrenHeight]);

    const handleLayout = useCallback(() => {
      if (!scrollViewRef.current) {
        return;
      }
      setContainerWidth(scrollViewRef.current.clientWidth - 1); // Subtract 1 to avoid horizontal scrollbar issues
      setViewportHeight(window.innerHeight);

      if (!childrenRef.current) {
        return;
      }
      setChildrenHeight(childrenRef.current.getBoundingClientRect().height ?? 0);
    }, []);

    const handleScroll = useCallback(() => {
      if (!scrollViewRef.current) {
        return;
      }

      const scrollTop = scrollViewRef.current.scrollTop;
      const scrollHeight = scrollViewRef.current.scrollHeight;
      const clientHeight = scrollViewRef.current.clientHeight;

      setScrollOffset(scrollTop);

      const scrollPosition = scrollTop + clientHeight;
      const thresholdPosition = scrollHeight - window.innerHeight;

      if (scrollPosition >= thresholdPosition && !isLoading && typeof onEndReached === 'function') {
        onEndReached();
      }

      if (onScroll) {
        onScroll({ scrollTop, scrollHeight, clientHeight });
      }
    }, [onEndReached, isLoading, onScroll]);

    /**
     * Effect to automatically trigger `onEndReached` if the display area is not yet scrollable.
     * Ensures enough items are loaded to initially fill the viewport and enable scrolling.
     */
    useEffect(() => {
      if (
        scrollViewRef.current &&
        scrollViewRef.current.scrollHeight <= scrollViewRef.current.clientHeight &&
        typeof onEndReached === 'function' &&
        !isLoading
      ) {
        onEndReached();
      }
    }, [items.length, onEndReached, isLoading]);

    useEffect(() => {
      const node = scrollViewRef.current;
      if (!node) {
        return;
      }

      node.addEventListener('scroll', handleScroll);
      handleLayout();

      window.addEventListener('resize', handleLayout);
      return () => {
        node.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleLayout);
      };
    }, [handleScroll, handleLayout]);

    useImperativeHandle(ref, () => scrollViewRef.current);

    /**
     * We need to loop over the child components to determine which ones should be "sticky".
     * `position: sticky` only works for direct children of the scrollable container, so
     * these are rendered outside the `childrenRef` div.
     */
    const childEntries = useMemo(
      () =>
        React.Children.toArray(children).map((child) => {
          return { child, isSticky: !!child.props.isSticky };
        }),
      [children]
    );

    return (
      <div ref={scrollViewRef} style={{ overflowY: 'scroll', height: '100vh', width: '100vw', position: 'relative' }}>
        <div ref={childrenRef}>
          {childEntries.map(({ child, isSticky }, index) =>
            isSticky ? null : <React.Fragment key={index}>{child}</React.Fragment>
          )}
        </div>
        {childEntries.map(({ child, isSticky }, index) =>
          isSticky ? <React.Fragment key={index}>{child}</React.Fragment> : null
        )}
        {items.length === 0 && ListEmptyComponent}
        <div
          style={{
            width: containerWidth,
            height: previousConfig.current.containerHeight,
            position: 'relative',
          }}
        >
          {visibleItems.map(({ item, x, y, width, height }) => (
            <div
              key={item.id}
              style={{
                position: 'absolute',
                left: x,
                top: y,
                width,
                height,
              }}
            >
              {item.component}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export default MasonryList;
